import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import PaginatedTable from "../Common/PaginatedTable";
import { Link } from "react-router-dom";
import { HiEye, HiXCircle } from "react-icons/hi";
import { Equipment } from "../../Models/Equipment";
import { formatNumbers } from "../../Utils";
import { FileTypeEnums } from "../../Models/FileType";
import blankImage from "../../Assets/NoPhoto.jpg";
import { Select, Tooltip } from "flowbite-react";
import { SavedItem } from "../../Models/SavedItem";

export default function SavedItemsTable({
  data,
  removeFromSaved,
  moveToCart,
}: {
  data: SavedItem[];
  removeFromSaved(id: number): void;
  moveToCart(item: SavedItem): void;
}) {
  const columnHelper = createColumnHelper<SavedItem>();

  const columns = [
    columnHelper.accessor((row) => row.equipmentId, {
      id: "id",
      header: "",
      cell: (info) => (
        <div className="md:flex gap-1">
          <Link to={`/products/equipment/${info.getValue()}`}>
            <img
              key={info.row.original.id}
              src={
                info.row.original.equipment?.files &&
                info.row.original.equipment?.files?.filter(
                  (x) => x.fileTypeId === FileTypeEnums.Photo
                ).length > 0
                  ? info.row.original.equipment?.files.filter(
                      (x) => x.fileTypeId === FileTypeEnums.Photo
                    )[0].fileUrl
                  : blankImage
              }
              alt={info.row.original.equipment?.number + "-image"}
              className="h-[75px] object-contain"
            />
          </Link>
          <div className="flex flex-col justify-center">
            <i>
              Manufacturer: {info.row.original.equipment?.manufacturer?.name}
            </i>
            <i>Number: {info.row.original.equipment?.number}</i>
          </div>
        </div>
      ),
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.equipment?.price, {
      id: "price",
      header: "Price",
      cell: (info) => {
        const equipment = info.row.original.equipment;
        const isTrade = equipment?.oemvendor?.startsWith("1");
        const price = isTrade
          ? equipment?.tradePrice ?? 0
          : equipment?.special1Price ?? 0;

        return <i>${formatNumbers(price)}</i>;
      },
      size: 35,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.equipment?.price, {
      id: "priceTotal",
      header: "Price Total",
      cell: (info) => (
        <i>${formatNumbers(info.row.original.equipment?.price ?? "")}</i>
      ),
      size: 35,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.id, {
      id: "removeId",
      header: "",
      cell: (info) => (
        <div className="flex">
          <Tooltip content={"Remove from saved"}>
            <button
              type="button"
              className="buttonCancel p-2"
              onClick={() => removeFromSaved(info.getValue())}
            >
              <HiXCircle />
            </button>
          </Tooltip>
          <button
            type="button"
            className="text-blue-600 rounded-md px-1 hover:bg-gray-100"
            onClick={() => moveToCart(info.row.original)}
          >
            Move to cart
          </button>
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <div className="w-full mx-auto overflow-x-auto">
        <PaginatedTable
          table={table}
          dataCount={data.length}
          columnResizeMode="onChange"
          showFilters={true}
          showPagination={false}
        />
      </div>
    </>
  );
}
